import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScSpinner from '../ScSpinner';
import { fetchProfileActivityStages, saveProfileActivityStages, fetchStages } from '../../studyConfigurationApi';

const sortStages = (stages) =>
  [...stages].sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0));

const ProfileStagesTab = ({ projectCode, profiles }) => {
  const [stageColumns, setStageColumns] = useState([]);
  const [accessByProfileNo, setAccessByProfileNo] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draftCodes, setDraftCodes] = useState(null);

  const sortedProfiles = useMemo(
    () => [...(profiles || [])].sort((a, b) => (a.profileCode || '').localeCompare(b.profileCode || '')),
    [profiles]
  );

  const loadAccessForProfile = useCallback(async (profileNo) => {
    const res = await fetchProfileActivityStages(projectCode, profileNo);
    const data = res.data || {};
    return {
      profileNo,
      profileCode: data.profileCode,
      profileActivityMappingNo: data.profileActivityMappingNo ?? 0,
      stageCodes: data.stageCodes || [],
    };
  }, [projectCode]);

  const loadAll = useCallback(async () => {
    if (!projectCode || !sortedProfiles.length) {
      setStageColumns([]);
      setAccessByProfileNo({});
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const stagesRes = await fetchStages(projectCode);
      setStageColumns(sortStages(stagesRes.data || []));

      const accessList = await Promise.all(
        sortedProfiles.map((p) => loadAccessForProfile(p.profileNo))
      );
      const map = {};
      accessList.forEach((a) => {
        map[a.profileNo] = a;
      });
      setAccessByProfileNo(map);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load profile stage access.');
    } finally {
      setLoading(false);
    }
  }, [projectCode, sortedProfiles, loadAccessForProfile]);

  useEffect(() => {
    setEditingKey(null);
    setDraftCodes(null);
    loadAll();
  }, [loadAll]);

  const startEdit = (profile) => {
    const access = accessByProfileNo[profile.profileNo];
    setEditingKey(profile.profileNo);
    setDraftCodes([...(access?.stageCodes || [])]);
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftCodes(null);
  };

  const toggleDraftStage = (stageCode) => {
    setDraftCodes((prev) =>
      (prev || []).includes(stageCode)
        ? prev.filter((c) => c !== stageCode)
        : [...(prev || []), stageCode]
    );
  };

  const reloadProfile = async (profileNo) => {
    const access = await loadAccessForProfile(profileNo);
    setAccessByProfileNo((prev) => ({ ...prev, [profileNo]: access }));
  };

  const handleSave = async () => {
    if (editingKey == null || draftCodes == null) return;
    const profileNo = editingKey;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await saveProfileActivityStages(projectCode, {
        profileNo,
        stageCodes: draftCodes,
      });
      setEditingKey(null);
      setDraftCodes(null);
      await reloadProfile(profileNo);
      setSuccess('Stage access saved.');
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = (profileNo) => editingKey != null && editingKey === profileNo;

  const stageCodesForRow = (profileNo, editable) => {
    if (editable && draftCodes) return draftCodes;
    return accessByProfileNo[profileNo]?.stageCodes || [];
  };

  const renderStageCell = (profile, stage, editable) => {
    const codes = stageCodesForRow(profile.profileNo, editable);
    const label = `${stage.displayName} (${stage.stageCode})`;
    return (
      <td key={stage.stageCode} className="sc-flag-cell">
        <input
          type="checkbox"
          checked={codes.includes(stage.stageCode)}
          disabled={!editable || saving}
          onChange={() => editable && toggleDraftStage(stage.stageCode)}
          aria-label={label}
        />
      </td>
    );
  };

  const renderActionsCell = (profile) => {
    const editable = isEditing(profile.profileNo);

    if (editable) {
      return (
        <td className="sc-actions-cell">
          <button
            type="button"
            className="sc-btn sc-btn-primary sc-btn-sm"
            disabled={saving}
            onClick={handleSave}
          >
            Save
          </button>
          <button
            type="button"
            className="sc-btn sc-btn-secondary sc-btn-sm"
            disabled={saving}
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </td>
      );
    }

    return (
      <td className="sc-actions-cell">
        <button
          type="button"
          className="sc-btn sc-btn-link"
          disabled={editingKey != null}
          onClick={() => startEdit(profile)}
        >
          Edit
        </button>
      </td>
    );
  };

  if (!projectCode) {
    return (
      <p className="sc-empty">
        No project in session. Change project from Visit or Site pages (shown in the header).
      </p>
    );
  }

  if (loading) return <ScSpinner />;

  return (
    <div className="sc-profile-stage-access">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}

      {sortedProfiles.length === 0 ? (
        <p className="sc-empty">No profiles available.</p>
      ) : (
        <div className="sc-grid-wrap">
          <table className="sc-grid sc-flag-grid">
            <thead>
              <tr>
                <th>Profile</th>
                {stageColumns.map((s) => (
                  <th key={s.stageCode}>
                    {s.displayName} ({s.stageCode})
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProfiles.map((profile) => {
                const editable = isEditing(profile.profileNo);
                return (
                  <tr
                    key={profile.profileNo}
                    className={editable ? 'sc-row-editing' : ''}
                  >
                    <td>{profile.profileCode ?? '—'}</td>
                    {stageColumns.map((s) => renderStageCell(profile, s, editable))}
                    {renderActionsCell(profile)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {stageColumns.length === 0 && sortedProfiles.length > 0 && (
        <p className="sc-empty">No workflow stages configured for this project.</p>
      )}
    </div>
  );
};

export default ProfileStagesTab;
